import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { testCaseAPI, datasetAPI } from '../services/api';

function TestCaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [testCase, setTestCase] = useState(null);
  const [dataset, setDataset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [availableDatasets, setAvailableDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState(null);

  useEffect(() => {
    fetchTestCase();
  }, [id]);

  const fetchTestCase = async () => {
    try {
      setLoading(true);
      const response = await testCaseAPI.getById(id);
      setTestCase(response.data);

      // If test case uses a dataset, fetch dataset info
      if (response.data.datasetId) {
        try {
          const datasetResponse = await datasetAPI.getById(response.data.datasetId);
          setDataset(datasetResponse.data);
        } catch (err) {
          console.error('Failed to fetch dataset:', err);
        }
      }

      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this test case?')) {
      return;
    }

    try {
      await testCaseAPI.delete(id);
      navigate('/');
    } catch (err) {
      alert('Failed to delete test case: ' + err.message);
    }
  };

  const handleEdit = async () => {
    setEditedData({
      name: testCase.name,
      project: testCase.project,
      description: testCase.description || '',
      endpoint: testCase.endpoint,
      method: testCase.method,
      auth: testCase.auth || { type: 'none' },
      success_codes: testCase.success_codes || [200],
      timeout: testCase.timeout || 30,
      datasetId: testCase.datasetId || null,
      csvMapping: testCase.csvMapping || {
        pathColumn: '',
        queryColumns: {},
        bodyColumns: {},
        headersColumns: {}
      }
    });
    setIsEditing(true);

    // Fetch available datasets
    try {
      const response = await datasetAPI.getAll();
      setAvailableDatasets(response.data);
    } catch (err) {
      console.error('Failed to fetch datasets:', err);
    }

    // If dataset is selected, fetch it to get columns
    if (testCase.datasetId) {
      try {
        const response = await datasetAPI.getById(testCase.datasetId);
        setSelectedDataset(response.data);
      } catch (err) {
        console.error('Failed to fetch dataset:', err);
      }
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedData(null);
    setSelectedDataset(null);
  };

  const handleDatasetChange = async (e) => {
    const datasetId = e.target.value;
    setEditedData(prev => ({
      ...prev,
      datasetId: datasetId || null
    }));

    if (datasetId) {
      try {
        const response = await datasetAPI.getById(datasetId);
        setSelectedDataset(response.data);
      } catch (err) {
        console.error('Failed to fetch dataset:', err);
        setSelectedDataset(null);
      }
    } else {
      setSelectedDataset(null);
    }
  };

  const handleMappingChange = (e) => {
    const { name, value } = e.target;
    setEditedData(prev => ({
      ...prev,
      csvMapping: {
        ...prev.csvMapping,
        [name]: value
      }
    }));
  };

  const handleParamMappingChange = (type, paramName, columnName) => {
    setEditedData(prev => {
      const newMapping = { ...prev.csvMapping };
      if (!newMapping[type]) {
        newMapping[type] = {};
      }

      if (columnName) {
        newMapping[type] = {
          ...newMapping[type],
          [paramName]: columnName
        };
      } else {
        const updatedType = { ...newMapping[type] };
        delete updatedType[paramName];
        newMapping[type] = updatedType;
      }

      return {
        ...prev,
        csvMapping: newMapping
      };
    });
  };

  const addParamMapping = (type) => {
    const paramName = prompt(`Enter parameter name for ${type}:`);
    if (paramName) {
      handleParamMappingChange(type, paramName, '');
    }
  };

  const removeParamMapping = (type, paramName) => {
    setEditedData(prev => {
      const newMapping = { ...prev.csvMapping };
      if (newMapping[type]) {
        const updatedType = { ...newMapping[type] };
        delete updatedType[paramName];
        newMapping[type] = updatedType;
      }
      return {
        ...prev,
        csvMapping: newMapping
      };
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAuthChange = (e) => {
    const { name, value } = e.target;
    setEditedData(prev => ({
      ...prev,
      auth: {
        ...prev.auth,
        [name]: value
      }
    }));
  };

  const handleSuccessCodesChange = (e) => {
    const rawValue = e.target.value;

    const codes = rawValue.split(',')
        .map(c => c.trim())
        .filter(c => c !== '')
        .map(c => parseInt(c))
        .filter(c => !isNaN(c));

    setEditedData(prev => ({
      ...prev,
      success_codes: codes,
      success_codes_input: rawValue
    }));
  };
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const response = await testCaseAPI.update(id, editedData);
      setTestCase(response.data);
      setIsEditing(false);
      setEditedData(null);
    } catch (err) {
      setError('Failed to save changes: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !testCase) {
    return (
      <div className="card">
        <div className="alert alert-danger">
          {error || 'Test case not found'}
        </div>
        <Link to="/" className="btn btn-secondary">Back to Test Cases</Link>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="flex-between mb-3">
          <h2>{isEditing ? 'Edit Test Case' : testCase.name}</h2>
          <div className="flex">
            {!isEditing ? (
              <>
                <button onClick={handleEdit} className="btn btn-primary">
                  Edit
                </button>
                <Link to={`/execute/${testCase._id}`} className="btn btn-success">
                  Run Tests
                </Link>
                <button onClick={handleDelete} className="btn btn-danger">
                  Delete
                </button>
                <Link to="/" className="btn btn-secondary">
                  Back
                </Link>
              </>
            ) : (
              <>
                <button onClick={handleSave} className="btn btn-success" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button onClick={handleCancelEdit} className="btn btn-secondary" disabled={saving}>
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        {isEditing ? (
          <div>
            <h3>Edit Configuration</h3>
            <div className="grid grid-2">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  value={editedData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Project</label>
                <input
                  type="text"
                  name="project"
                  value={editedData.project}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Endpoint URL</label>
                <input
                  type="url"
                  name="endpoint"
                  value={editedData.endpoint}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Method</label>
                <select
                  name="method"
                  value={editedData.method}
                  onChange={handleInputChange}
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                  <option value="DELETE">DELETE</option>
                </select>
              </div>

              <div className="form-group">
                <label>Success Codes (comma-separated)</label>
                <input
                  type="text"
                  name="success_codes_input"
                  value={editedData.success_codes_input || editedData.success_codes.join(', ')}
                  onChange={handleSuccessCodesChange}
                />
              </div>

              <div className="form-group">
                <label>Timeout (seconds)</label>
                <input
                  type="number"
                  name="timeout"
                  value={editedData.timeout}
                  onChange={handleInputChange}
                  min="1"
                />
              </div>

              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Description</label>
                <textarea
                  name="description"
                  value={editedData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>
            </div>

            <h3 className="mt-3">Authentication</h3>
            <div className="grid grid-2">
              <div className="form-group">
                <label>Authentication Type</label>
                <select
                  name="type"
                  value={editedData.auth.type}
                  onChange={handleAuthChange}
                >
                  <option value="none">None</option>
                  <option value="bearer">Bearer Token</option>
                  <option value="api_key">API Key</option>
                  <option value="basic">Basic Auth</option>
                </select>
              </div>

              {editedData.auth.type === 'bearer' && (
                <div className="form-group">
                  <label>Bearer Token</label>
                  <input
                    type="password"
                    name="token"
                    value={editedData.auth.token || ''}
                    onChange={handleAuthChange}
                    placeholder="Enter bearer token"
                  />
                </div>
              )}

              {editedData.auth.type === 'api_key' && (
                <>
                  <div className="form-group">
                    <label>Header Name</label>
                    <input
                      type="text"
                      name="key_name"
                      value={editedData.auth.key_name || ''}
                      onChange={handleAuthChange}
                      placeholder="e.g., X-API-Key"
                    />
                  </div>
                  <div className="form-group">
                    <label>API Key Value</label>
                    <input
                      type="password"
                      name="key_value"
                      value={editedData.auth.key_value || ''}
                      onChange={handleAuthChange}
                      placeholder="Enter API key"
                    />
                  </div>
                </>
              )}

              {editedData.auth.type === 'basic' && (
                <>
                  <div className="form-group">
                    <label>Username</label>
                    <input
                      type="text"
                      name="username"
                      value={editedData.auth.username || ''}
                      onChange={handleAuthChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      name="password"
                      value={editedData.auth.password || ''}
                      onChange={handleAuthChange}
                    />
                  </div>
                </>
              )}
            </div>

            <h3 className="mt-3">Dataset & Mapping Configuration</h3>
            <div className="form-group">
              <label>Dataset</label>
              <select
                value={editedData.datasetId || ''}
                onChange={handleDatasetChange}
              >
                <option value="">No dataset (use inline tests)</option>
                {availableDatasets.map(ds => (
                  <option key={ds._id} value={ds._id}>
                    {ds.name} ({ds.rowCount} rows)
                  </option>
                ))}
              </select>
              <small>Select a dataset to use as test data source</small>
            </div>

            {editedData.datasetId && selectedDataset && (
              <div className="mt-3">
                <h4>CSV Column Mapping</h4>
                <p className="text-muted">Map dataset columns to request parameters</p>

                <div className="form-group">
                  <label>Path Column</label>
                  <select
                    name="pathColumn"
                    value={editedData.csvMapping.pathColumn || ''}
                    onChange={handleMappingChange}
                  >
                    <option value="">None</option>
                    {selectedDataset.columns?.map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                  <small>Column to use for URL path (e.g., /users/:id)</small>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Query Parameters</span>
                    <button
                      type="button"
                      onClick={() => addParamMapping('queryColumns')}
                      className="btn btn-sm btn-secondary"
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    >
                      + Add Query Param
                    </button>
                  </label>
                  {Object.entries(editedData.csvMapping.queryColumns || {}).map(([paramName, columnName]) => (
                    <div key={paramName} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <input
                        type="text"
                        value={paramName}
                        readOnly
                        style={{ flex: '0 0 200px' }}
                        placeholder="Parameter name"
                      />
                      <select
                        value={columnName}
                        onChange={(e) => handleParamMappingChange('queryColumns', paramName, e.target.value)}
                        style={{ flex: 1 }}
                      >
                        <option value="">Select column</option>
                        {selectedDataset.columns?.map(col => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => removeParamMapping('queryColumns', paramName)}
                        className="btn btn-sm btn-danger"
                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <small>Map columns to query parameters (e.g., ?search=value)</small>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Body Parameters</span>
                    <button
                      type="button"
                      onClick={() => addParamMapping('bodyColumns')}
                      className="btn btn-sm btn-secondary"
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    >
                      + Add Body Param
                    </button>
                  </label>
                  {Object.entries(editedData.csvMapping.bodyColumns || {}).map(([paramName, columnName]) => (
                    <div key={paramName} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <input
                        type="text"
                        value={paramName}
                        readOnly
                        style={{ flex: '0 0 200px' }}
                        placeholder="Parameter name"
                      />
                      <select
                        value={columnName}
                        onChange={(e) => handleParamMappingChange('bodyColumns', paramName, e.target.value)}
                        style={{ flex: 1 }}
                      >
                        <option value="">Select column</option>
                        {selectedDataset.columns?.map(col => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => removeParamMapping('bodyColumns', paramName)}
                        className="btn btn-sm btn-danger"
                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <small>Map columns to request body fields (JSON)</small>
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Headers</span>
                    <button
                      type="button"
                      onClick={() => addParamMapping('headersColumns')}
                      className="btn btn-sm btn-secondary"
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    >
                      + Add Header
                    </button>
                  </label>
                  {Object.entries(editedData.csvMapping.headersColumns || {}).map(([paramName, columnName]) => (
                    <div key={paramName} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <input
                        type="text"
                        value={paramName}
                        readOnly
                        style={{ flex: '0 0 200px' }}
                        placeholder="Header name"
                      />
                      <select
                        value={columnName}
                        onChange={(e) => handleParamMappingChange('headersColumns', paramName, e.target.value)}
                        style={{ flex: 1 }}
                      >
                        <option value="">Select column</option>
                        {selectedDataset.columns?.map(col => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => removeParamMapping('headersColumns', paramName)}
                        className="btn btn-sm btn-danger"
                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <small>Map columns to HTTP headers</small>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-2">
            <div>
              <h3>Configuration</h3>
              <p><strong>Project:</strong> {testCase.project}</p>
              <p><strong>Endpoint:</strong> {testCase.endpoint}</p>
              <p><strong>Method:</strong> <span className="badge badge-info">{testCase.method}</span></p>
              <p><strong>Total Tests:</strong> {testCase.totalTests || testCase.tests?.length || 0}</p>
              <p><strong>Success Codes:</strong> {testCase.success_codes?.join(', ')}</p>
              <p><strong>Timeout:</strong> {testCase.timeout}s</p>
              {testCase.description && (
                <p><strong>Description:</strong> {testCase.description}</p>
              )}
            </div>

            <div>
              <h3>Authentication</h3>
              <p><strong>Type:</strong> {testCase.auth?.type || 'none'}</p>
              {testCase.auth?.type === 'bearer' && (
                <p><strong>Token:</strong> {testCase.auth.token ? '••••••' : 'Not set'}</p>
              )}
              {testCase.auth?.type === 'api_key' && (
                <>
                  <p><strong>Header:</strong> {testCase.auth.key_name}</p>
                  <p><strong>Value:</strong> {testCase.auth.key_value ? '••••••' : 'Not set'}</p>
                </>
              )}
              {testCase.auth?.type === 'basic' && (
                <p><strong>Username:</strong> {testCase.auth.username}</p>
              )}
            </div>
          </div>
        )}

        {testCase.csvMapping && (
          <div className="mt-3">
            <h3>CSV Mapping</h3>
            <p><strong>Path Column:</strong> {testCase.csvMapping.pathColumn || 'None'}</p>

            {Object.keys(testCase.csvMapping.queryColumns || {}).length > 0 && (
              <div>
                <strong>Query Parameters:</strong>
                <ul>
                  {Object.entries(testCase.csvMapping.queryColumns).map(([param, col]) => (
                    <li key={param}>{param} → {col}</li>
                  ))}
                </ul>
              </div>
            )}

            {Object.keys(testCase.csvMapping.bodyColumns || {}).length > 0 && (
              <div>
                <strong>Body Parameters:</strong>
                <ul>
                  {Object.entries(testCase.csvMapping.bodyColumns).map(([param, col]) => (
                    <li key={param}>{param} → {col}</li>
                  ))}
                </ul>
              </div>
            )}

            {Object.keys(testCase.csvMapping.headersColumns || {}).length > 0 && (
              <div>
                <strong>Headers:</strong>
                <ul>
                  {Object.entries(testCase.csvMapping.headersColumns).map(([param, col]) => (
                    <li key={param}>{param} → {col}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="mt-3">
          <h3>Test Data Source</h3>
          {testCase.datasetId ? (
            <div>
              <div className="alert alert-info">
                <strong>Using Dataset:</strong> This test case uses a dataset for test data.
              </div>

              {dataset ? (
                <div>
                  <p><strong>Dataset Name:</strong> <Link to={`/datasets/${dataset._id}`}>{dataset.name}</Link></p>
                  <p><strong>Total Rows:</strong> {dataset.rowCount.toLocaleString()}</p>
                  <p><strong>Columns:</strong> {dataset.columns?.length || 0}</p>
                  {dataset.description && (
                    <p><strong>Description:</strong> {dataset.description}</p>
                  )}
                  {dataset.tags && dataset.tags.length > 0 && (
                    <p>
                      <strong>Tags:</strong>{' '}
                      {dataset.tags.map(tag => (
                        <span key={tag} className="badge badge-info" style={{ marginRight: '0.25rem' }}>
                          {tag}
                        </span>
                      ))}
                    </p>
                  )}

                  <div className="mt-2">
                    <h4>Dataset Preview (First 5 rows)</h4>
                    <div style={{ overflowX: 'auto' }}>
                      <pre style={{ background: '#f5f5f5', padding: '1rem', borderRadius: '4px', maxHeight: '400px', overflowY: 'auto' }}>
                        {JSON.stringify(dataset.data.slice(0, 5), null, 2)}
                      </pre>
                    </div>
                    <Link to={`/datasets/${dataset._id}`} className="btn btn-secondary" style={{ marginTop: '0.5rem' }}>
                      View Full Dataset
                    </Link>
                  </div>
                </div>
              ) : (
                <p>Loading dataset information...</p>
              )}
            </div>
          ) : testCase.tests && testCase.tests.length > 0 ? (
            <div>
              <div className="alert alert-info">
                <strong>Inline Tests:</strong> This test case stores test data directly.
              </div>
              <p>Showing first 5 of {testCase.tests.length} tests</p>
              <div style={{ overflowX: 'auto' }}>
                <pre style={{ background: '#f5f5f5', padding: '1rem', borderRadius: '4px' }}>
                  {JSON.stringify(testCase.tests.slice(0, 5), null, 2)}
                </pre>
              </div>
            </div>
          ) : (
            <div className="alert alert-warning">
              No test data source configured
            </div>
          )}
        </div>

        <div className="mt-3">
          <p><strong>Created:</strong> {new Date(testCase.createdAt).toLocaleString()}</p>
          <p><strong>Last Updated:</strong> {new Date(testCase.updatedAt).toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
}

export default TestCaseDetail;
